import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { diagnosticsActions } from "./store/slice";
import { diagnosticsOperationSelectors } from "./store/selectors.operation";
import { DiagnosticsMetricsIndicators } from "./DiagnosticsMetricsIndicators";
import { useGetStreamDiagnosticsForOperationQuery } from "services/streamingApi/diagnostics.api";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { Box } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import "primereact/resources/themes/md-light-indigo/theme.css";
import "./Style/TreeTable.scss";
import { selectDiagnosticsConfiguration } from "features/config/store/selectors";
import { luxonUtils } from "utils/luxonUtils";

export interface DiagnosticsMetricsProps {
    diagnosticsCorrelationId?: string;
    showStartupFlow?: boolean;
}

export function DiagnosticsMetrics(props: DiagnosticsMetricsProps) {
    const diagnosticsConfiguration = useAppSelector(
        selectDiagnosticsConfiguration,
    );
    const diagnosticsLogTreeNodes = useAppSelector(
        diagnosticsOperationSelectors.selectDiagnosticsLogsAsTreeNodes,
    );
    const diagnosticsSettingsRefreshIntervalInSeconds = useAppSelector(
        diagnosticsOperationSelectors.selectDiagnosticsLogsRefreshIntervalInSeconds,
    );
    const diagnosticsSettingsQueryLookbackDuration = useAppSelector(
        diagnosticsOperationSelectors.selectDiagnosticsSettingsQueryLookbackDuration,
    );
    const serverTimeCallDurationInMilliseconds = useAppSelector(
        diagnosticsOperationSelectors.selectServerTimeCallDurationInMilliseconds,
    );
    const diagnosticsLogsLiveStreamStartUpFlow = useAppSelector(
        diagnosticsOperationSelectors.selectDiagnosticsLogsLiveStreamStartUpFlow,
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        return () => {
            // Cleanup captured logs when component is unmounted
            if (props.diagnosticsCorrelationId) {
                dispatch(diagnosticsActions.resetLogItems());
            }
        };
    }, []);

    // auto update diagnostics logs
    const { data, isError, error } = useGetStreamDiagnosticsForOperationQuery(
        {
            diagnosticsCorrelationId: props.diagnosticsCorrelationId,
            lookbackDuration:
                diagnosticsSettingsQueryLookbackDuration ?? "PT5m",
        },
        {
            pollingInterval: 1000 * diagnosticsSettingsRefreshIntervalInSeconds,
            skip: !props.diagnosticsCorrelationId,

            // Prevent polling when the tab is unfocused
            skipPollingIfUnfocused: true,
            refetchOnFocus: true,
        },
    );

    const browserPingDiagnosticsIndicatorProps = useMemo(() => {
        return [
            {
                label: "Browser Ping",
                duration: serverTimeCallDurationInMilliseconds,
                description: "Time taken to fetch the current server time.",
                error: false,
            },
        ];
    }, [data, serverTimeCallDurationInMilliseconds]);

    const latestFullyFetchedVideoPartDiagnosticsIndicatorProps = useMemo(() => {
        const latestFullyFetchedVideoPartLog = data?.diagnosticsLogNodes?.find(
            (log) =>
                (log.logInfo.name.indexOf("Video Part") > -1 ||
                    log.logInfo.name.indexOf("Video Segment") > -1) &&
                log.duration !== 0 &&
                log.durationBackFromLastChild !== 0 &&
                log.durationBackFromLastChild !== 0,
        );

        return [
            {
                label: "Latest Video Part",
                duration: latestFullyFetchedVideoPartLog?.duration,
                description: "Time taken to fetch the latest video part.",
                error:
                    diagnosticsConfiguration?.maxDurationInMsLatestVideoPart &&
                    latestFullyFetchedVideoPartLog?.duration
                        ? latestFullyFetchedVideoPartLog?.duration >
                          diagnosticsConfiguration.maxDurationInMsLatestVideoPart
                        : false,
                durationToFirstChild:
                    latestFullyFetchedVideoPartLog?.durationToFirstChild,
                durationBackFromLastChild:
                    latestFullyFetchedVideoPartLog?.durationBackFromLastChild,
            },
        ];
    }, [data, serverTimeCallDurationInMilliseconds]);
    const diagnosticsStartupFlow = useMemo(() => {
        if (!diagnosticsLogsLiveStreamStartUpFlow) {
            return [];
        }

        return diagnosticsLogsLiveStreamStartUpFlow.map(
            (livestreamStartupFlowSteps) => {
                return {
                    label: livestreamStartupFlowSteps.name || "Unknown Step",
                    duration: livestreamStartupFlowSteps.duration,
                    description: livestreamStartupFlowSteps.description,
                    error:
                        livestreamStartupFlowSteps.maxAllowedDuration &&
                        livestreamStartupFlowSteps.duration
                            ? livestreamStartupFlowSteps.duration >
                              livestreamStartupFlowSteps.maxAllowedDuration
                            : false,
                };
            },
        );
    }, [diagnosticsLogsLiveStreamStartUpFlow]);

    // Notify user if initial data is loaded, but a refresh failed.
    useEffect(() => {
        if (isError && !data) {
            enqueueSnackbar(
                `Failed to refresh Intermittent diagnostic logs, retrying.`,
                {
                    variant: "warning",
                },
            );
        }
    }, [isError, data]);

    return (
        <Box sx={{ marginTop: 8 }}>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
                <DiagnosticsMetricsIndicators
                    diagnosticsMetricsIndicators={
                        browserPingDiagnosticsIndicatorProps
                    }
                />
                <DiagnosticsMetricsIndicators
                    diagnosticsMetricsIndicators={
                        latestFullyFetchedVideoPartDiagnosticsIndicatorProps
                    }
                />
            </Box>
            {props.showStartupFlow === true && (
                <DiagnosticsMetricsIndicators
                    diagnosticsMetricsIndicators={diagnosticsStartupFlow}
                />
            )}
            <TreeTable
                value={diagnosticsLogTreeNodes}
                paginator
                rows={15}
                totalRecords={diagnosticsLogTreeNodes?.length || 0}
                columnResizeMode="expand"
                emptyMessage="No diagnostic logs available yet"
                className="dense-treetable"
            >
                <Column
                    field="initiator"
                    header="Initiator"
                    body={(value) =>
                        value.data.initiator.replace(
                            "BasicFit.Security.VideoManagementSystem.",
                            "",
                        )
                    }
                    sortable
                    expander
                ></Column>
                <Column
                    field="name"
                    header="Name"
                    sortable
                ></Column>
                <Column
                    field="duration"
                    header="Duration (ms)"
                    body={(value) => value.data.duration.toFixed(0)}
                    sortable
                    style={{ width: "160px" }}
                ></Column>
                <Column
                    field="timeGenerated"
                    header="Time"
                    body={(value) =>
                        luxonUtils
                            .fromIso(value.data.timeGenerated)
                            ?.toFormat("HH:mm:ss:SSS")
                    }
                    sortable
                    style={{ width: "200px" }}
                ></Column>
                <Column
                    field="success"
                    header="Status"
                    body={(value) =>
                        value.data.success ? (
                            <CheckCircleIcon sx={{ color: "green" }} />
                        ) : (
                            <ErrorIcon sx={{ color: "red" }} />
                        )
                    }
                    sortable
                    style={{ width: "120px" }}
                ></Column>
            </TreeTable>

            {isError && !data && (
                <GlobalErrorMessage
                    title={"Unable to fetch initial diagnostic logs"}
                    message={
                        "Please contact the service desk if the problem persists."
                    }
                    debugData={error}
                    alertSeverity={"error"}
                />
            )}
        </Box>
    );
}
